import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';
import type { InputNumberRef } from '@rc-component/input-number';
import { forwardRef, useCallback } from 'react';
import type { KeyboardEvent, ClipboardEvent } from 'react';
import type { InputContext } from './TextInput';
import { filterNumberInputKey, handleNumberInputPaste } from './keyFilter';
import { CONTROL_CLASS } from '@/theme/controlSizing';

/**
 * Props {@link PercentInput}.
 *
 * Tidak meng-expose `formatter`/`parser`/`controls`/`precision`/`addonAfter`
 * karena sudah dikunci sesuai spek Percent. Properti lain (termasuk `min`,
 * `max`, `disabled`, dll.) diteruskan ke Antd `<InputNumber>`.
 */
export interface PercentInputProps
  extends Omit<
    InputNumberProps<number>,
    | 'formatter'
    | 'parser'
    | 'controls'
    | 'precision'
    | 'addonAfter'
    | 'size'
    | 'stringMode'
  > {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: InputContext;
  /** Override paksa size Antd jika perlu. */
  size?: InputNumberProps['size'];
}

/**
 * SIBLING dari `NumberInput`: Textbox untuk **persentase** (mis. Tarif PPh,
 * Tarif PPN, Persentase Termin, Diskon %).
 *
 * Spek (BERBEDA dengan {@link NumberInput}):
 * - Underlying: Antd `<InputNumber>`.
 * - 2 desimal (`precision = 2`).
 * - Separator desimal Indonesia (koma).
 * - **Tanpa stepper** (`controls = false`).
 * - **`addonAfter="%"`** otomatis (tidak boleh di-override).
 * - **Range default 0 – 100** — bisa di-override via prop `min`/`max` jika
 *   diperlukan kasus khusus (mis. PPh tertentu yang ada bracket > 100%).
 * - **Nilai 0 ditampilkan apa adanya** (`'0'`) — `0%` punya makna sah
 *   (mis. "Tarif PPN 0% untuk ekspor").
 * - Rata kanan + width 100% (konsisten dengan family).
 *
 * **Kapan pakai ini?**
 * - Field pajak/diskon/persentase yang nilainya rasio (0-100% normalnya).
 *
 * **Catatan format:**
 * Tidak memakai separator ribuan karena nilai persentase normalnya kecil
 * (< 100). Jika memang perlu (kasus sangat langka), gunakan Antd `InputNumber`
 * langsung dengan formatter custom.
 *
 * Pemakaian:
 * ```tsx
 * <PercentInput value={tarifPPh} onChange={setTarifPPh} />
 * <PercentInput value={diskonPersen} onChange={setDiskonPersen} max={50} />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/textbox-pattern.md`
 */
export const PercentInput = forwardRef<InputNumberRef, PercentInputProps>(
  function PercentInput(
    {
      context = 'form',
      size,
      style,
      className,
      min = 0,
      max = 100,
      onKeyDown,
      onPaste,
      ...rest
    },
    ref
  ) {
    const resolvedSize = size ?? (context === 'page' ? 'small' : 'middle');
    // Percent = decimal (boleh koma untuk presisi tarif).
    const filterOptions = { allowComma: true };

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (filterNumberInputKey(e, filterOptions)) return;
        onKeyDown?.(e);
      },
      [filterOptions, onKeyDown]
    );

    const handlePaste = useCallback(
      (e: ClipboardEvent<HTMLInputElement>) => {
        handleNumberInputPaste(e, filterOptions);
        onPaste?.(e);
      },
      [filterOptions, onPaste]
    );

    return (
      <InputNumber<number>
        ref={ref}
        size={resolvedSize}
        controls={false}
        precision={2}
        decimalSeparator=","
        addonAfter="%"
        min={min}
        max={max}
        className={`${CONTROL_CLASS} booku-number-input${className ? ` ${className}` : ''}`}
        style={{ width: '100%', ...style }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        {...rest}
      />
    );
  }
);
